import { useEffect } from "react";

/**
 * Lightweight SEO setter — updates document.title, meta description,
 * canonical, Open Graph and Twitter Card tags.
 */
export function useSEO({ title, description, canonical, image }) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (selector, attr, attrValue, content) => {
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, attrValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    if (description) {
      setMeta('meta[name="description"]', "name", "description", description);
      setMeta(
        'meta[property="og:description"]',
        "property",
        "og:description",
        description,
      );
      setMeta(
        'meta[name="twitter:description"]',
        "name",
        "twitter:description",
        description,
      );
    }

    if (title) {
      setMeta('meta[property="og:title"]', "property", "og:title", title);
      setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    }

    // Defaults that don't depend on per-page input
    setMeta('meta[property="og:type"]', "property", "og:type", "website");
    setMeta(
      'meta[property="og:site_name"]',
      "property",
      "og:site_name",
      "Disposable Smart Labels",
    );
    setMeta(
      'meta[name="twitter:card"]',
      "name",
      "twitter:card",
      "summary_large_image",
    );

    if (canonical) {
      const href = canonical.startsWith("http")
        ? canonical
        : `${window.location.origin}${canonical}`;

      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", href);

      setMeta('meta[property="og:url"]', "property", "og:url", href);
    }

    if (image) {
      const absImage = image.startsWith("http")
        ? image
        : `${window.location.origin}${image}`;
      setMeta('meta[property="og:image"]', "property", "og:image", absImage);
      setMeta(
        'meta[name="twitter:image"]',
        "name",
        "twitter:image",
        absImage,
      );
    }
  }, [title, description, canonical, image]);
}

/**
 * Inject a JSON-LD <script> tag into <head>. Replaces the previous tag for the same id on unmount.
 * Pass an object or array; it will be JSON.stringified.
 */
export function useJsonLd(id, data) {
  const serialized = data ? JSON.stringify(data) : null;
  useEffect(() => {
    if (!serialized || !id) return undefined;
    const tagId = `jsonld-${id}`;
    let el = document.getElementById(tagId);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = tagId;
      document.head.appendChild(el);
    }
    el.textContent = serialized;
    return () => {
      const cur = document.getElementById(tagId);
      if (cur) cur.remove();
    };
  }, [id, serialized]);
}

/* Reusable schema builders */
export const SITE_URL =
  process.env.REACT_APP_SITE_URL || "https://disposablesmartlabels.com";

export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Disposable Smart Labels",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description:
    "Programme dedicated to disposable smart labels for shipment visibility, with operational delivery powered by GoAndTrack.",
  email: "hello@disposablesmartlabels.com",
  sameAs: [],
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Disposable Smart Labels",
  url: SITE_URL,
  publisher: { "@type": "Organization", name: "Disposable Smart Labels" },
};

/**
 * Build a BreadcrumbList schema payload.
 *   trail = [{ name: "Home", url: "/" }, { name: "Disposable GPS Tracker" }]
 */
export function breadcrumbSchema(trail) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t.name,
      item: t.url ? `${SITE_URL}${t.url}` : undefined,
    })),
  };
}

/**
 * Build a FAQPage schema payload from an array of Q/A pairs.
 * Accepts answer values as strings; strips any HTML.
 */
export function faqSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

/**
 * Build an Article schema payload for a technical guide.
 */
export function articleSchema({
  headline,
  description,
  path,
  image,
  datePublished,
  dateModified,
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    image: image ? [image] : undefined,
    datePublished,
    dateModified: dateModified || datePublished,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}${path}`,
    },
    author: { "@type": "Organization", name: "Disposable Smart Labels" },
    publisher: {
      "@type": "Organization",
      name: "Disposable Smart Labels",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/logo.png`,
      },
    },
  };
}
